import React, { useState, useEffect } from "react";
import type { ActionPlanTask, ActionPlanTaskPage, TaskDetailsResponse } from "../../../services/actionPlanService";
import { getTaskDetails } from "../../../services/actionPlanService";
import { apiService } from "../../../services/api";

interface TaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: ActionPlanTask | null;
}

const formatGoalLabel = (goal?: string) => {
  if (!goal) return '';
  const goalMap: Record<string, string> = {
    'domain-authority': 'Improve Domain Authority',
    'organic-traffic': 'Increase Organic Traffic',
    'keyword-rankings': 'Improve Keyword Rankings',
    'serp-features': 'Captured Visibility',
    'avg-position': 'Reduce Average Position',
    'impressions': 'Increase Impressions',
  };
  return goal
    .split(',')
    .map((g) => {
      const trimmed = g.trim();
      return goalMap[trimmed] || trimmed.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    })
    .join(', ');
};

const formatSubParamLabel = (key?: string) => {
  if (!key) return '';
  const subMap: Record<string, string> = {
    blog_creation: 'SEO Blog Articles',
    faq_creation: 'Keyword-Optimized FAQs',
    offpage_asset_distribution: 'Off-Page Authority Assets',
    authority_score: 'Domain Authority',
    referring_domains_count: 'Referring Domains Count',
    link_source_quality_distribution: 'Backlink Quality Distribution',
    follow_vs_nofollow_backlinks: 'Follow vs Nofollow Backlinks',
    spam_score: 'Spam Score',
    image_alt_text: 'Image Alt Text',
    image_file_naming: 'Image File Naming',
    lazy_loading_issues: 'Lazy Loading Issues',
    accessibility_compliance: 'Accessibility Compliance',
    meta_description_presence_quality: 'Meta Description Presence & Quality',
    pixel_length_issues: 'Pixel Length Issues',
    h1_presence_uniqueness: 'H1 Presence & Uniqueness',
    heading_order_issues: 'Heading Order Issues',
    crawl_budget_efficiency: 'Crawl Budget Efficiency',
  };
  return subMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

export default function TaskDetailsModal({ isOpen, onClose, task }: TaskDetailsModalProps) {
  const [liveDetails, setLiveDetails] = useState<TaskDetailsResponse | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [siteUrl, setSiteUrl] = useState<string>("");

  const userId = localStorage.getItem("user_id") || "";

  useEffect(() => {
    if (!isOpen || !userId) return;
    const fetchSiteUrl = async () => {
      try {
        const res = await apiService.getUserProfile(userId);
        const data = res?.data || res;
        if (data?.website_url) {
          setSiteUrl(data.website_url);
        }
      } catch (err) {
        console.warn("Could not load user profile for siteUrl", err);
      }
    };
    fetchSiteUrl();
  }, [isOpen, userId]);

  const handleCopyUrl = (urlToCopy: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(urlToCopy);
    setCopiedUrl(urlToCopy);
    setTimeout(() => {
      setCopiedUrl((prev) => (prev === urlToCopy ? null : prev));
    }, 2000);
  };

  const getEffectiveSiteUrl = () => {
    if (siteUrl && siteUrl.startsWith("http")) return siteUrl;
    if (siteUrl) return `https://${siteUrl}`;
    // Fallback from task pages
    if (task?.pages) {
      for (const p of task.pages) {
        if (p.page_url && p.page_url.startsWith("http") && !p.page_url.match(/\.(png|jpg|jpeg|webp|svg|gif)$/i)) {
          return p.page_url;
        }
      }
    }
    return "https://yourwebsite.com";
  };

  const renderAssetUrlCell = (pageUrl: string, idx: number, currentVal?: string) => {
    if (!pageUrl || pageUrl === "—" || pageUrl === "-") {
      return <span className="text-gray-400 dark:text-gray-500">—</span>;
    }

    const isInline =
      pageUrl.startsWith("data:") ||
      pageUrl.startsWith("blob:") ||
      pageUrl.startsWith("javascript:") ||
      pageUrl.includes("svg%3E") ||
      (currentVal && currentVal.includes("svg%3E"));

    const resolvedSite = getEffectiveSiteUrl();

    if (isInline) {
      return (
        <div className="flex flex-col gap-1.5 py-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60">
              <i className="ri-code-s-slash-line text-amber-600 dark:text-amber-400 text-sm"></i>
              Inline &lt;svg&gt; #{idx + 1}
            </span>
            <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">Template Asset</span>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 leading-snug">
            <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">Found on:</span>
            <a
              href={resolvedSite}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 font-mono break-all"
              title={`Open ${resolvedSite} to inspect this inline asset`}
            >
              <span>{resolvedSite.replace(/^https?:\/\//, '')}</span>
              <i className="ri-external-link-line text-xs flex-shrink-0"></i>
            </a>
          </div>
        </div>
      );
    }

    // Fully qualified or relative URL
    let fullUrl = pageUrl;
    if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
      if (fullUrl.startsWith("//")) {
        fullUrl = `https:${fullUrl}`;
      } else if (fullUrl.startsWith("/")) {
        const baseOrigin = resolvedSite.replace(/\/$/, "");
        fullUrl = `${baseOrigin}${fullUrl}`;
      } else if (fullUrl.startsWith("www.") || (fullUrl.includes(".") && !fullUrl.includes("/") && !fullUrl.match(/\.(png|jpg|jpeg|webp|svg|gif|ico|bmp|avif)$/i))) {
        // External domain e.g. www.example.com
        fullUrl = `https://${fullUrl}`;
      } else {
        // Relative path or image filename e.g. "wp-content/uploads/..." or "image.png"
        const baseOrigin = resolvedSite.replace(/\/$/, "");
        fullUrl = `${baseOrigin}/${fullUrl.replace(/^\.\//, "")}`;
      }
    }

    const isCopied = copiedUrl === fullUrl;

    // Extract filename for clean display badge
    let filenameBadge = "";
    try {
      const u = new URL(fullUrl);
      const cleanPath = u.pathname.replace(/\/$/, "");
      const fn = cleanPath.split("/").pop();
      if (fn && fn.includes(".")) {
        filenameBadge = fn;
      }
    } catch {
      // ignore url parse error
    }

    return (
      <div className="flex flex-col gap-1 py-0.5 max-w-sm">
        <div className="flex items-start gap-1.5 group">
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline break-all inline-flex items-center gap-1 leading-relaxed"
            title={`Click to open ${fullUrl} in new tab`}
          >
            <span>{fullUrl}</span>
            <i className="ri-external-link-line flex-shrink-0 text-sm opacity-70 group-hover:opacity-100"></i>
          </a>
          <button
            type="button"
            onClick={(e) => handleCopyUrl(fullUrl, e)}
            title={isCopied ? "Copied!" : "Copy URL to clipboard"}
            className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
          >
            {isCopied ? (
              <i className="ri-check-line text-xs text-green-500"></i>
            ) : (
              <i className="ri-file-copy-line text-xs"></i>
            )}
          </button>
        </div>
        {filenameBadge && (
          <div className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="text-gray-400 font-normal">Asset:</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700/60 px-1.5 py-0.2 rounded text-gray-700 dark:text-gray-300 font-medium">
              {filenameBadge}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderCurrentValueCell = (val: string, pageUrl: string, idx: number) => {
    if (!val || val === "—" || val === "-") {
      return <span className="text-gray-400 dark:text-gray-500">—</span>;
    }

    const isInline =
      pageUrl?.startsWith("data:") ||
      pageUrl?.startsWith("blob:") ||
      pageUrl?.startsWith("javascript:") ||
      pageUrl?.includes("svg%3E") ||
      val.includes("svg%3E");

    // Clean any legacy corrupted replacement artifacts
    const sanitizedVal = val;

    // Check if it's a lazy loading issue
    if (sanitizedVal.toLowerCase().includes("loading=") || sanitizedVal.toLowerCase().includes("lazy")) {
      if (isInline) {
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold text-red-600 dark:text-red-400">
              loading="lazy" attribute missing
            </span>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">
              Inline &lt;svg&gt; element #{idx + 1}
            </span>
          </div>
        );
      }
      const fnMatch = sanitizedVal.match(/filename:\s*([^,\s—]+)/);
      const cleanFn = fnMatch && fnMatch[1] !== "svg%3E" && fnMatch[1] !== "N/A" ? fnMatch[1] : "";
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold text-red-600 dark:text-red-400">
            loading="lazy" attribute not set
          </span>
          {cleanFn && (
            <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">
              Filename: {cleanFn}
            </span>
          )}
        </div>
      );
    }

    // Check if it's an alt text issue
    if (sanitizedVal.toLowerCase().includes("alt=") || sanitizedVal.toLowerCase().includes("alt")) {
      if (isInline) {
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold text-red-600 dark:text-red-400">
              Missing alt attribute (alt="")
            </span>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">
              Inline vector icon #{idx + 1}
            </span>
          </div>
        );
      }
      const fnMatch = sanitizedVal.match(/filename:\s*([^,\s—]+)/);
      const cleanFn = fnMatch && fnMatch[1] !== "svg%3E" && fnMatch[1] !== "N/A" ? fnMatch[1] : "";
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold text-red-600 dark:text-red-400">
            Empty or missing alt text (alt="")
          </span>
          {cleanFn && (
            <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">
              Filename: {cleanFn}
            </span>
          )}
        </div>
      );
    }

    // Check if it's an image filename naming issue
    if (sanitizedVal.includes("filename:")) {
      const cleanVal = sanitizedVal
        .replace(/filename:\s*svg%3E/g, "Generic inline SVG")
        .replace(/filename:\s*N\/A/g, "Generic / unoptimized filename");
      return <span className="text-xs text-gray-700 dark:text-gray-300 font-mono">{cleanVal}</span>;
    }

    return <span className="font-mono text-xs text-gray-600 dark:text-gray-300">{sanitizedVal}</span>;
  };

  const renderUrlCell = (url: string) => {
    if (!url || url === "—" || url === "-") {
      return <span className="text-gray-400 dark:text-gray-500">—</span>;
    }

    let href = url;
    if (!href.startsWith("http://") && !href.startsWith("https://")) {
      if (href.startsWith("//")) {
        href = `https:${href}`;
      } else if (href.startsWith("/")) {
        const baseOrigin = getEffectiveSiteUrl().replace(/\/$/, "");
        href = `${baseOrigin}${href}`;
      } else if (href.startsWith("www.") || (href.includes(".") && !href.match(/\.(png|jpg|jpeg|webp|svg|gif|ico|bmp|avif)$/i))) {
        href = `https://${href}`;
      } else {
        const baseOrigin = getEffectiveSiteUrl().replace(/\/$/, "");
        href = `${baseOrigin}/${href.replace(/^\.\//, "")}`;
      }
    }
    const isCopied = copiedUrl === href;

    return (
      <div className="flex items-start gap-2 group max-w-sm">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline break-all inline-flex items-center gap-1 leading-relaxed"
          title={`Click to open ${href} in new tab`}
        >
          <span>{url}</span>
          <i className="ri-external-link-line flex-shrink-0 text-sm opacity-70 group-hover:opacity-100"></i>
        </a>
        <button
          type="button"
          onClick={(e) => handleCopyUrl(href, e)}
          title={isCopied ? "Copied!" : "Copy asset link"}
          className="opacity-70 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
        >
          {isCopied ? (
            <i className="ri-check-line text-xs text-green-500"></i>
          ) : (
            <i className="ri-file-copy-line text-xs"></i>
          )}
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (!isOpen || !task) {
      setLiveDetails(null);
      setDetailsError(null);
      return;
    }
    const needsLiveFetch = !task.pages || task.pages.length === 0;
    if (!needsLiveFetch) return;

    const fetchDetails = async () => {
      setDetailsLoading(true);
      setDetailsError(null);
      try {
        const data = await getTaskDetails(userId, task.id);
        setLiveDetails(data);
      } catch (err) {
        setDetailsError("Unable to load live details. Please try again.");
      } finally {
        setDetailsLoading(false);
      }
    };
    fetchDetails();
  }, [isOpen, task?.id]);

  if (!isOpen || !task) return null;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "text-red-600 bg-red-100 border-red-200";
      case "medium": return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "low": return "text-green-600 bg-green-100 border-green-200";
      default: return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "text-green-600 bg-green-100 border-green-200";
      case "in_progress": return "text-blue-600 bg-blue-100 border-blue-200";
      default: return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getCWVStatusColor = (status: string) => {
    if (status?.toLowerCase() === "good") return "text-green-700 bg-green-100";
    if (status?.toLowerCase() === "needs improvement") return "text-yellow-700 bg-yellow-100";
    if (status?.toLowerCase() === "poor") return "text-red-700 bg-red-100";
    return "text-gray-500 bg-gray-100";
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between bg-gray-50 dark:bg-gray-800/50">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">{task.title}</h2>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wider ${getPriorityColor(task.priority)}`}>
                {task.priority} Priority
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wider ${getStatusColor(task.status)}`}>
                {task.status.replace("_", " ")}
              </span>
              {task.category && (
                <span className="px-3 py-1 rounded-full text-xs font-bold border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  {task.category}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">

          {/* Metadata Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-600">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider mb-1">Impact Score</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{task.impact_score}/100</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-600">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider mb-1">Effort Score</div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{task.effort_score}/100</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-600">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider mb-1">Affected Pages</div>
              <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">{task.affected_pages_count}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-600">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider mb-1">Effort Level</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">{task.effort_description || "Unknown"}</div>
            </div>
          </div>

          {/* Context */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {task.parameter_group && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <i className="ri-folder-line text-blue-500"></i> Parameter Group
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">{task.parameter_group}</p>
              </div>
            )}
            {task.sub_parameter && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <i className="ri-list-check text-purple-500"></i> Sub Parameter
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">{formatSubParamLabel(task.sub_parameter)}</p>
              </div>
            )}
            {task.related_goal && (
              <div className="md:col-span-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <i className="ri-flag-line text-teal-500"></i> Related Goal
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">{formatGoalLabel(task.related_goal)}</p>
              </div>
            )}
          </div>

          {/* Descriptions */}
          <div className="space-y-6 border-t border-gray-200 dark:border-gray-700 pt-6">
            {task.impact_description && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <i className="ri-lightbulb-flash-line text-yellow-500"></i> Why this matters (Impact)
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{task.impact_description}</p>
              </div>
            )}
            {task.description && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <i className="ri-file-text-line text-blue-500"></i> Task Description
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{task.description}</p>
              </div>
            )}
            {task.recommendation && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/50">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-3 flex items-center gap-2">
                  <i className="ri-robot-line text-blue-600 dark:text-blue-400 text-lg"></i> AI Recommendation &amp; Next Steps
                </h4>
                <div className="text-sm text-blue-800 dark:text-blue-300 leading-relaxed whitespace-pre-wrap">{task.recommendation}</div>
              </div>
            )}
          </div>

          {/* Live Details Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">

            {/* On-Page: existing pages[] table */}
            {task.pages && task.pages.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-search-eye-line text-orange-500 text-lg"></i>
                  Affected Assets &amp; Pages
                  <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-700">
                    {task.pages.length} issue{task.pages.length !== 1 ? "s" : ""} found
                  </span>
                </h4>

                {/* Inline Asset Guidance Banner */}
                {task.pages.some((p: ActionPlanTaskPage) =>
                  p.page_url?.startsWith("data:") ||
                  p.page_url?.startsWith("blob:") ||
                  p.page_url?.startsWith("javascript:") ||
                  p.page_url?.includes("svg%3E") ||
                  p.current_value?.includes("svg%3E")
                ) && (
                  <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-50/90 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-xs text-amber-900 dark:text-amber-200 mb-4">
                    <i className="ri-information-line text-base text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <span className="font-semibold">Locating Inline &lt;svg&gt; Assets on your Website:</span> Inline SVG elements and embedded data images are located directly within your webpage template/HTML source code (rather than hosted as separate image files). To inspect and fix them, open your page URL below in a browser, right-click &rarr; <strong>Inspect</strong>, and search for the &lt;svg&gt; or &lt;img&gt; tag to add <code className="bg-amber-100 dark:bg-amber-900/60 px-1 py-0.5 rounded font-mono text-[11px]">loading="lazy"</code> or a descriptive <code className="bg-amber-100 dark:bg-amber-900/60 px-1 py-0.5 rounded font-mono text-[11px]">alt="..."</code> attribute.
                    </div>
                  </div>
                )}

                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/60 text-left">
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/3">Asset / URL</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/4">Issue Found</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/4">Current Value</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recommended Fix</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {task.pages.map((page: ActionPlanTaskPage, idx: number) => (
                        <tr key={page.id ?? idx} className="hover:bg-orange-50/40 dark:hover:bg-orange-900/10 transition-colors">
                          <td className="px-4 py-3 align-top">
                            {renderAssetUrlCell(page.page_url, idx, page.current_value)}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                              <i className="ri-error-warning-line text-sm flex-shrink-0"></i>
                              {page.issue_description || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-top">
                            {renderCurrentValueCell(page.current_value, page.page_url, idx)}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span className="text-xs text-green-700 dark:text-green-400 font-medium leading-relaxed block">
                              {page.recommended_value || "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 italic">Showing all {task.pages.length} specific assets/pages affected by this issue.</p>
              </div>
            )}

            {/* Loading */}
            {detailsLoading && (
              <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-500 dark:text-gray-400">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-sm font-medium">
                  {task.category === "technical" ? "Running live audit across all pages..." : "Fetching live data..."}
                </p>
                <p className="text-xs text-gray-400">This may take 10-30 seconds</p>
              </div>
            )}

            {/* Error */}
            {detailsError && !detailsLoading && (
              <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
                <i className="ri-error-warning-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-sm font-semibold text-red-800 dark:text-red-300">Could not load live data</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{detailsError}</p>
                </div>
              </div>
            )}

            {/* Live Table */}
            {!detailsLoading && liveDetails && liveDetails.detail_type === "table" && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                  <i className={`text-lg ${task.category === "offpage" ? "ri-links-line text-violet-500" : "ri-speed-line text-blue-500"}`}></i>
                  {liveDetails.title}
                </h4>
                {"description" in liveDetails && liveDetails.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">{liveDetails.description}</p>
                )}
                {"summary" in liveDetails && liveDetails.summary && Object.keys(liveDetails.summary).length > 0 && (
                  <div className="flex flex-wrap gap-3 mb-4">
                    {Object.entries(liveDetails.summary).map(([label, val]) => (
                      <div key={label} className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-center">
                        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">{val}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
                  <table className="w-full text-sm min-w-max">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/60 text-left">
                        {"columns" in liveDetails && liveDetails.columns.map((col: string) => (
                          <th key={col} className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {"rows" in liveDetails && liveDetails.rows.map((row: Record<string, string | number>, idx: number) => (
                        <tr key={idx} className="hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-colors">
                          {"columns" in liveDetails && liveDetails.columns.map((col: string) => {
                            const val = String(row[col] ?? "-");
                            const isStatus = col.toLowerCase().includes("status");
                            const isAction = col.toLowerCase() === "action";
                            const isUrl = col.toLowerCase().includes("url") || col.toLowerCase() === "page url";
                            return (
                              <td key={col} className="px-4 py-3 align-top">
                                {isUrl ? (
                                  renderUrlCell(val)
                                ) : isStatus ? (
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getCWVStatusColor(val)}`}>{val}</span>
                                ) : isAction ? (
                                  <span className="text-xs text-orange-700 dark:text-orange-400 font-medium">{val}</span>
                                ) : (
                                  <span className="text-xs text-gray-700 dark:text-gray-300">{val}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {"rows" in liveDetails && (
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 italic">
                    Showing {liveDetails.rows.length} result{liveDetails.rows.length !== 1 ? "s" : ""} from live data.
                  </p>
                )}
              </div>
            )}

            {/* Action Item */}
            {!detailsLoading && liveDetails && liveDetails.detail_type === "action_item" && (
              <div className="bg-amber-50 dark:bg-amber-900/20 p-5 rounded-xl border border-amber-200 dark:border-amber-800/50">
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-amber-600 dark:text-amber-400 text-lg"></i>
                  {liveDetails.title}
                </h4>
                <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">{liveDetails.text}</p>
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500 border-t border-gray-200 dark:border-gray-700 pt-4 mt-8">
            {task.created_at && <span>Created: {new Date(task.created_at).toLocaleDateString()}</span>}
            {task.updated_at && <span>Last Updated: {new Date(task.updated_at).toLocaleDateString()}</span>}
            {task.completed_at && <span>Completed: {new Date(task.completed_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-end">
          <button onClick={onClose} className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
            Close Details
          </button>
        </div>
      </div>
    </div>
  );
}

